import { Button, Modal, Select, Table } from 'antd';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { CloseModalSVG } from 'components/iconsSVG/close';
import { useI18n } from 'context/i18n';
import { array, string } from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';
import { SOCKS_SIZE_ORDER, STANDARD_SIZE_GUIDE, COUNTRIES_USING_INCHES, DEFAULT_GUIDE_UNITS, UNIT_LABELS_MAPPER } from 'utils/constants/sizes';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { getMarket, getTarget } from 'utils/helpers';
import { columns } from './guide';
import { MeasureCamper, MeasureNnormal } from './measure';
import { getDomainValue } from 'utils/domain';
import { isBlank } from 'utils/StringUtils';

import styles from './style.module.css';
import type { AutoString } from 'types/utils';

type Units = 'cm' | 'mm' | 'in';
type SizeNodeKey = 'guideSizes' | 'guideSizesAlt';

type ShoeGuideRow = { country: string; size: string };
type ApparelGuideRow = { size: string; sizeValue: string };

type ShoeGuide = {
  targetId: string;
  target: string;
  productType: AutoString<'shoes'>;
  guideSizes: Record<string, ShoeGuideRow[]>;
  guideSizesAlt?: Record<string, ShoeGuideRow[]>;
  equivalences?: Record<string, string>;
  socksSizes?: Record<string, string[]>;
};

type ApparelGuide = {
  targetId: string;
  target: string;
  productType: Exclude<string, 'shoes'>;
  guideSizes: Record<string, ApparelGuideRow[]>;
};

type GuideItem = ShoeGuide | ApparelGuide;

type SizesGuideProps = {
  guide: GuideItem[];
  code: string;
  target: string;
  currentCountry: string;
  name: string;
  sizeGuideInfo: string;
};

function SizesGuide({ guide, code, target, currentCountry, name, sizeGuideInfo }: SizesGuideProps) {
  const { t, locale } = useI18n();
  const market = getMarket(locale);

  const targets = guide.map((item) => item.targetId);
  const defaultTarget = targets.indexOf(target);

  const defaultTab = defaultTarget >= 0 ? defaultTarget : 0;

  const columnsToUse = useRef(columns as { baseColumns: any[] });

  if (typeof (guide[0] as Partial<ShoeGuide>)?.equivalences !== 'object') {
    // nnormal no es zapato
    const cols: any[] = [
      {
        title: '',
        dataIndex: 'part',
        key: 'part',
        render: (text: string) => t('ficha.producto', `size.apparel.part.${text}`, text),
      },
    ];
    const sizes = new Set(
      (Object.values((guide[0] as ApparelGuide).guideSizes) as ApparelGuideRow[][])
        .flat()
        .filter((s: ApparelGuideRow) => !isBlank(s.size) && !isBlank(s.sizeValue))
        .map(({ size }) => size),
    );
    sizes.forEach((size: string) => {
      cols.push({
        title: size,
        dataIndex: size,
        key: size,
      });
    });
    columnsToUse.current = { baseColumns: cols };
  }

  const defaultUnits: Units =
    COUNTRIES_USING_INCHES.includes(market) ? 'in'
    : market === 'KR' ? 'mm'
    : 'cm';

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [units, setUnits] = useState<Units>(defaultUnits);
  const [sizeNode, setSizeNode] = useState<SizeNodeKey>('guideSizes');

  const defaultGuide = guide.find((item) => item.targetId === targets[defaultTarget]) ?? guide?.[0];
  let defaultCountryColumns: string[] = [];
  if (defaultGuide && (defaultGuide as any)[sizeNode]) {
    const dg = (defaultGuide as any)[sizeNode] as Record<string, { country: string }[]>;
    const firstKey = Object.keys(dg)[0];
    if (firstKey) {
      defaultCountryColumns = dg[firstKey]?.map((row: { country: string }) => row.country) ?? [];
    } else {
      defaultCountryColumns = [];
    }
  }
  const defaultNonStandard = defaultCountryColumns.filter((countryItem) => !STANDARD_SIZE_GUIDE.includes(countryItem));
  const defaultSelectedCountry: string | null = defaultNonStandard.includes(market) ? market : null;
  const [selectedCountry, setSelectedCountry] = useState<string | null>(defaultSelectedCountry);

  const showModal = () => {
    setIsModalVisible(true);
    if (!defaultGuide) return;
    const targetValue = getTarget(defaultGuide.targetId, 2);
    dataLayerHandleEvent({
      event: 'item_size_guide',
      event_category: 'product detail',
      event_detail_1: 'open',
      event_detail_2: targetValue,
    });
    const eventGA4 = {
      event: 'shoe_size_guide',
      item_name: name,
      item_id: code,
    };
    dataLayerHandleEvent(eventGA4);
  };
  const handleCancel = () => {
    dataLayerHandleEvent({
      event: 'item_size_guide',
      event_category: 'product detail',
      event_detail_1: 'close',
      event_detail_2: '',
    });
    setIsModalVisible(false);
  };
  const onChangeTab = (key: number) => {
    const item = guide[key];
    if (!item) return;
    const { targetId } = item;
    const targetValue = getTarget(targetId, 2);

    dataLayerHandleEvent({
      event: 'item_size_guide',
      event_category: 'product detail',
      event_detail_1: targetValue,
      event_detail_2: '',
    });

    setActiveTab(key);
  };

  /**
   * Extra la talla en tipo float a partir de un string
   * La talla puede llegar como "23cm", "23cm / 9.1in" o "22.5cm / 8.9in"
   * Lo importante es el número de delante
   */
  const extractSizeFloat = (sizeStr: string) => {
    const rx = /^(\d\.?)+/g;
    const arr = rx.exec(sizeStr);
    try {
      return parseFloat(arr?.[0] ?? '0');
    } catch (e) {
      console.error(e);
      return 0;
    }
  };

  /**
   * Ordena las tallas de menor a mayor
   */
  const sortGuideSizes = (a: string, b: string) => {
    const numA = extractSizeFloat(a);
    const numB = extractSizeFloat(b);
    if (numA === numB) {
      return 0;
    }
    return numA > numB ? 1 : -1;
  };

  useEffect(() => {
    // como antd mete estilos inline que nos afectan al overflow
    // lo tenemos que modificar
    // por suerte nos dará tiempo antes de que el user llegue a abrir el modal
    if (isModalVisible) {
      const tableContent = document.querySelectorAll('div.ant-table-content');
      tableContent.forEach((content: Element) => {
        // @ts-ignore
        content.style.overflow = 'unset';
      });
    }
  }, [isModalVisible]);

  useEffect(() => {
    if (process.browser) {
      const existingColumns = columnsToUse.current.baseColumns;
      const newBaseColumns: any[] = [
        {
          ...existingColumns[0],
          key: `feet.length${UNIT_LABELS_MAPPER[units as keyof typeof UNIT_LABELS_MAPPER]}`,
          title: '' as string,
          defaultValue: '***Foot length ()' as string,
        },
        ...existingColumns.slice(1),
      ];
      columnsToUse.current = { baseColumns: newBaseColumns };
      setSizeNode((DEFAULT_GUIDE_UNITS as readonly Units[]).includes(units) ? 'guideSizes' : 'guideSizesAlt');
    }
  }, [units]);

  const tabButtons = guide.map((sizeItem, index) => (
    <Button
      className={styles.targetTab}
      onClick={() => {
        onChangeTab(index);
      }}
      // @ts-ignore antd
      type={activeTab === index ? 'primary' : 'secondary'}
      key={`tab-${index}-${String(sizeItem.targetId)}`}
    >
      {String(sizeItem.target)}
    </Button>
  ));

  const dataRowSortMethod = (a: string, b: string) => {
    const [formattedA, formattedB] = [a.replace(',', '.'), b.replace(',', '.')];
    const headA = formattedA.includes('-') ? (formattedA.split('-')[0] ?? formattedA) : formattedA;
    const headB = formattedB.includes('-') ? (formattedB.split('-')[0] ?? formattedB) : formattedB;
    const [parsedA, parsedB] = [parseFloat(headA), parseFloat(headB)];

    if (Number.isNaN(parsedA)) {
      return 1;
    }

    if (Number.isNaN(parsedB)) {
      return 1;
    }

    if (parsedA === parsedB) {
      return 0;
    }

    return parsedA > parsedB ? 1 : -1;
  };

  return (
    <>
      <div className={styles.sizeGuideIconWrapper}>
        {getDomainValue({
          camper: <img onClick={showModal} src="/assets-new/sizeguide.png" alt="Size Guide Icon" width={20} height={20} />,
        })}
        <span
          onClick={showModal}
          className={getDomainValue({
            camper: `${styles.sizesGuideSpan}`,
            nnormal: `${styles.sizesGuideSpan}`,
            camperlab: 'text xs uppercase underline',
          })}
        >
          {t('ficha.producto', 'size.fit')}
        </span>
      </div>
      {/* @ts-ignore antd */}
      <Modal
        title={null}
        visible={isModalVisible}
        footer={null}
        width="80%"
        onCancel={handleCancel}
        closeIcon={<CloseModalSVG />}
        wrapClassName={`${styles.modalWrapper} `}
      >
        <div>
          {getDomainValue({
            camper: (
              <>
                <div className="[@media(min-width:1400px)]:hidden">
                  <Select
                    className={`${styles.targetSelector} `}
                    onChange={(v) => onChangeTab(Number(v))}
                    suffixIcon={<ChevronSVG />}
                    defaultValue={guide[defaultTab]?.target ?? guide[0]?.target}
                  >
                    {guide.map((sizeItem, index) => (
                      <Select.Option key={`target-option-${sizeItem.targetId}`} value={index}>
                        {sizeItem.target}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="[@media(max-width:1400px)]:hidden">
                  <div className={styles.tabButtons}>{tabButtons}</div>
                </div>
                <div className={styles.measureToggle} onClick={() => setUnits((current) => (current === 'cm' ? 'in' : 'cm'))}>
                  <span>{t('ficha.producto', 'size.units.cm', 'CM')}</span>

                  <div className={styles.toggleOval}>
                    <button className={`${styles.toggleEllipse} ${units === 'cm' ? styles.ellipseLeft : styles.ellipseRight}`} />
                  </div>

                  <span>{t('ficha.producto', 'size.units.in', 'IN')}</span>
                </div>
              </>
            ),
            nnormal: (
              <div className={styles.sizesGuideHeader}>
                <p className={styles.sizesGuideTitle}>{t('ficha.producto', 'size.guide.title', 'Size guide')}</p>
                <div className={styles.tabContainer}>{tabButtons}</div>
              </div>
            ),
          })}

          <div className={styles.customTab}>
            <div className={styles.tableAndMeasureContainer}>
              {guide.map((sizeGuide, index) => {
                let tableColumns: any[] | null = null;
                let dataSourceRows: any[] | null = null;
                if ((sizeGuide as ShoeGuide).equivalences) {
                  const sgAny = sizeGuide as any;
                  const firstKey2 = Object.keys(sgAny[sizeNode] || {})[0];
                  const countryColumns: string[] = firstKey2 ? (sgAny[sizeNode][firstKey2]?.map((row: { country: string }) => row.country) ?? []) : [];

                  columnsToUse.current.baseColumns.forEach((column: any) => {
                    if (column && column.title === '') {
                      const { key, defaultValue } = column;
                      const k = typeof key === 'string' ? key : '';
                      const def = typeof defaultValue === 'string' ? defaultValue : '';
                      const translatedTitle = String(t('ficha.producto', k, def));
                      column.title = translatedTitle;
                    }
                  });

                  const nonStandardCountries = countryColumns.filter((countryItem) => !STANDARD_SIZE_GUIDE.includes(countryItem));

                  const countrySelector = (
                    <Select
                      //@ts-ignore antd!
                      autoComplete="none"
                      showSearch
                      className={styles.countrySelector}
                      placeholder={t('modal.idiomas', 'select.country', 'Select a country')}
                      value={selectedCountry ?? undefined}
                      allowClear={false}
                      suffixIcon={<ChevronSVG />}
                      onChange={(item: string) => setSelectedCountry(item)}
                    >
                      {nonStandardCountries.map((item: string, countryIndex: number) => (
                        <Select.Option key={`non-standard-country-${countryIndex}`} value={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  );

                  tableColumns = [
                    ...columnsToUse.current.baseColumns,
                    ...countryColumns
                      .filter((countryItem) => STANDARD_SIZE_GUIDE.includes(countryItem))
                      .map((country: string) => ({
                        title: country === currentCountry ? <div>{country}</div> : country,
                        dataIndex: country,
                        key: country,
                      })),
                    {
                      title: countrySelector,
                      dataIndex: selectedCountry === null ? 'country-select' : selectedCountry,
                      key: 'country-select-column',
                      render: (value: string) => {
                        const obj: any = {
                          children: value,
                          props: {
                            className: 'selected-country-cell',
                          },
                        };
                        return obj;
                      },
                    },
                  ];
                  let currentSockSize: string | null = null;
                  let assignedCurrentSockSize = false;
                  let currentSockSizeIndex: number | null = null;
                  let lastCurrentSockSize: string | null = null;
                  if ((sizeGuide as ShoeGuide)?.socksSizes && Object.keys((sizeGuide as ShoeGuide).socksSizes as Record<string, string[]>).length) {
                    tableColumns.push({
                      title: t('ficha.producto', 'socks'),
                      dataIndex: 'sockSize',
                      key: 'sockSize',
                      render: (value: string, row: any) => {
                        const obj: any = {
                          children: value,
                          props: {
                            className: 'socks',
                          },
                        };
                        if (row.sockSize) {
                          obj.props.rowSpan = row.sockSizeSpan;
                        } else {
                          obj.props.rowSpan = 0;
                        }
                        return obj;
                      },
                    });
                    currentSockSize =
                      SOCKS_SIZE_ORDER.find((size) => Object.keys((sizeGuide as ShoeGuide).socksSizes as Record<string, string[]>).includes(size)) ?? null;
                    currentSockSizeIndex = currentSockSize !== null ? SOCKS_SIZE_ORDER.indexOf(currentSockSize) : null;
                    if (currentSockSize !== null) {
                      const ss = (sizeGuide as ShoeGuide).socksSizes as Record<string, string[]>;
                      const arr = ss[currentSockSize];
                      lastCurrentSockSize = arr ? (arr[arr.length - 1] ?? null) : null;
                    }
                  }
                  const sgRowsObj = (sizeGuide as any)[sizeNode] as Record<string, { country: string; size: string }[]>;
                  dataSourceRows = Object.keys(sgRowsObj)
                    .sort(IS_NNORMAL ? sortGuideSizes : dataRowSortMethod)
                    .map((footLength: string) => {
                      const footLengthToUse = market === 'KR' ? Number(footLength) / 10 : footLength;
                      const filteredRow = countryColumns
                        .map((country) => (sgRowsObj[footLength] || []).filter((row: { country: string }) => row.country === country))
                        .flat();
                      const countrysKeyValueObject = filteredRow.reduce(
                        (r: Record<string, string>, e: { country: string; size: string }) => {
                          r[e.country] = e.size;
                          return r;
                        },
                        {} as Record<string, string>,
                      );

                      const row: Record<string, any> = {
                        key: footLength,
                        footLength,
                        equivalent:
                          (sizeGuide as ShoeGuide).equivalences && String(footLengthToUse) in (sizeGuide as ShoeGuide).equivalences! ?
                            (sizeGuide as ShoeGuide).equivalences![String(footLengthToUse)]
                          : '',
                        ...countrysKeyValueObject,
                      };

                      if ((sizeGuide as ShoeGuide).socksSizes) {
                        if (!assignedCurrentSockSize) {
                          if (currentSockSize && (sizeGuide as ShoeGuide).socksSizes && (sizeGuide as ShoeGuide).socksSizes![currentSockSize]) {
                            row.sockSizeSpan = (sizeGuide as ShoeGuide).socksSizes![currentSockSize]!.length;
                            row.sockSize = currentSockSize;
                          }
                          assignedCurrentSockSize = true;
                        }
                        const isLastIndexSockSize = (row['EU'] as string | undefined) === (lastCurrentSockSize ?? undefined);
                        if (isLastIndexSockSize) {
                          currentSockSizeIndex = (currentSockSizeIndex ?? -1) + 1;
                          currentSockSize = currentSockSizeIndex !== null ? (SOCKS_SIZE_ORDER[currentSockSizeIndex] ?? null) : null;
                          if (currentSockSizeIndex !== null && currentSockSize && typeof SOCKS_SIZE_ORDER[currentSockSizeIndex] !== 'undefined') {
                            const ss = (sizeGuide as ShoeGuide).socksSizes as Record<string, string[]>;
                            const arr = ss[currentSockSize];
                            lastCurrentSockSize = arr ? (arr[arr.length - 1] ?? null) : null;
                            assignedCurrentSockSize = false;
                          }
                        }
                      }
                      const currVal = row[currentCountry];
                      if (currVal !== undefined && currVal !== null) {
                        row[currentCountry] = <div>{currVal}</div>;
                      }
                      return row;
                    });
                } else {
                  dataSourceRows = Object.entries((guide[0] as ApparelGuide).guideSizes).map(([part, partSizes]: [string, ApparelGuideRow[]]) => ({
                    part,
                    ...Object.fromEntries(partSizes.map(({ size, sizeValue }: ApparelGuideRow) => [size, sizeValue])),
                  }));
                  tableColumns = columnsToUse.current.baseColumns;
                }
                if (IS_CAMPER) {
                  dataSourceRows = (dataSourceRows || []).map((row: Record<string, any>) => {
                    Object.keys(row).map((key: string) => {
                      let value: string | string[] = '';
                      if (typeof row[key] === 'string') {
                        value = (row[key] as string).split(' / ');
                        if (value.length === 2) {
                          row[key] = value[units === 'cm' ? 0 : 1];
                        }
                      }
                      return value;
                    });
                    return row;
                  });
                }
                return (
                  <Table
                    key={`sizesGuide-table-${sizeGuide.targetId}`}
                    size="small"
                    footer={() => null}
                    dataSource={dataSourceRows as any[]}
                    columns={tableColumns as any[]}
                    pagination={false}
                    className={`${styles.customTable} ${IS_CAMPER && guide[0]?.productType !== 'shoes' ? styles.clothsTable : ''} ${index === activeTab ? '' : 'escondido'}`}
                    scroll={{ x: true }}
                  />
                );
              })}
              {IS_CAMPER ?
                <MeasureCamper type={guide[0]?.productType ?? 'shoes'} sizeGuideInfo={sizeGuideInfo} />
              : <MeasureNnormal type={guide[0]?.productType ?? 'shoes'} target={target} targetId={targets[activeTab] ?? targets[0] ?? ''} />}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

SizesGuide.propTypes = {
  guide: array,
  code: string,
  target: string,
  currentCountry: string,
  name: string,
  sizeGuideInfo: string,
};

export default SizesGuide;
